import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { apiClient } from '@/services/api/client';
import { socketManager } from '@/services/websocket/socketManager';
import { queryClient } from '@/app/queryClient';
import { LoginRequest, ChangePasswordRequest, ApiError } from '@/types/api';
import { toast } from 'sonner';

export type AuthStatus = 'INITIAL_CHECK' | 'AUTHENTICATED' | 'UNAUTHENTICATED' | 'OFFLINE_ERROR';

interface AuthContextType {
  status: AuthStatus;
  username: string | null;
  expiresAt: string | null;
  isLoggingIn: boolean;
  authError: string | null;
  rateLimitCountdown: number | null;
  login: (credentials: LoginRequest) => Promise<boolean>;
  logout: () => Promise<void>;
  changePassword: (req: ChangePasswordRequest) => Promise<boolean>;
  retryBootstrap: () => Promise<void>;
  clearAuthError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [status, setStatus] = useState<AuthStatus>('INITIAL_CHECK');
  const [username, setUsername] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [isLoggingIn, setIsLoggingIn] = useState<boolean>(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [rateLimitCountdown, setRateLimitCountdown] = useState<number | null>(null);

  const wasAuthenticatedRef = useRef<boolean>(false);
  const last401ToastRef = useRef<number>(0);
  const rateLimitTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearRateLimitTimer = useCallback(() => {
    if (rateLimitTimerRef.current) {
      clearInterval(rateLimitTimerRef.current);
      rateLimitTimerRef.current = null;
    }
  }, []);

  const startRateLimitCountdown = useCallback((seconds: number) => {
    clearRateLimitTimer();
    setRateLimitCountdown(seconds);
    rateLimitTimerRef.current = setInterval(() => {
      setRateLimitCountdown((prev) => {
        if (prev === null || prev <= 1) {
          clearRateLimitTimer();
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  }, [clearRateLimitTimer]);

  useEffect(() => {
    return () => {
      clearRateLimitTimer();
    };
  }, [clearRateLimitTimer]);

  const handleUnauthorized = useCallback(() => {
    // Only fire session expiry notification if an authenticated session was previously established
    if (wasAuthenticatedRef.current) {
      wasAuthenticatedRef.current = false;
      const now = Date.now();
      if (now - last401ToastRef.current > 3000) {
        last401ToastRef.current = now;
        toast.error('Session expired. Please log in again.');
      }
    }

    setStatus('UNAUTHENTICATED');
    setUsername(null);
    setExpiresAt(null);
    socketManager.disconnect();
    queryClient.clear();
  }, []);

  // Register the centralized 401 callback with API client
  useEffect(() => {
    apiClient.setOnUnauthorized(handleUnauthorized);
  }, [handleUnauthorized]);

  const bootstrapSession = useCallback(async () => {
    try {
      setStatus('INITIAL_CHECK');
      setAuthError(null);
      const res = await apiClient.getAuthMe();
      if (res && res.authenticated) {
        wasAuthenticatedRef.current = true;
        setStatus('AUTHENTICATED');
        setUsername(res.username || 'admin');
        setExpiresAt(res.expires_at || null);
        socketManager.connect();
      } else {
        wasAuthenticatedRef.current = false;
        setStatus('UNAUTHENTICATED');
        setUsername(null);
        setExpiresAt(null);
        socketManager.disconnect();
      }
    } catch (err: unknown) {
      wasAuthenticatedRef.current = false;
      const apiErr = err as ApiError;
      if (apiErr.status === 401) {
        setStatus('UNAUTHENTICATED');
        setUsername(null);
        setExpiresAt(null);
        socketManager.disconnect();
      } else {
        // Network or server error during bootstrap
        setStatus('OFFLINE_ERROR');
        socketManager.disconnect();
      }
    }
  }, []);

  useEffect(() => {
    bootstrapSession();
  }, [bootstrapSession]);

  const login = useCallback(async (credentials: LoginRequest): Promise<boolean> => {
    setIsLoggingIn(true);
    setAuthError(null);
    try {
      const res = await apiClient.login(credentials);
      if (res && res.success) {
        wasAuthenticatedRef.current = true;
        setStatus('AUTHENTICATED');
        setUsername(res.username || credentials.username || 'admin');
        setExpiresAt(res.expires_at || null);
        setAuthError(null);
        clearRateLimitTimer();
        setRateLimitCountdown(null);
        toast.success(`Welcome back, ${res.username || credentials.username || 'Operator'}`);
        socketManager.connect();
        return true;
      }
      setAuthError('Login failed. Please check your credentials.');
      return false;
    } catch (err: unknown) {
      const apiErr = err as ApiError;
      if (apiErr.status === 429) {
        const retrySec = apiErr.retry_after || 60;
        startRateLimitCountdown(retrySec);
        setAuthError(`Too many failed login attempts. Please try again in ${retrySec} seconds.`);
      } else if (apiErr.status === 401) {
        setAuthError(apiErr.message || 'Invalid username or password.');
      } else {
        setAuthError(apiErr.message || 'Unable to connect to authentication server.');
      }
      return false;
    } finally {
      setIsLoggingIn(false);
    }
  }, [clearRateLimitTimer, startRateLimitCountdown]);

  const logout = useCallback(async () => {
    wasAuthenticatedRef.current = false;
    try {
      await apiClient.logout();
    } catch {
      // Ignore network errors on logout - proceed with local cleanup
    } finally {
      setStatus('UNAUTHENTICATED');
      setUsername(null);
      setExpiresAt(null);
      socketManager.disconnect();
      queryClient.clear();
      toast.info('Logged out successfully.');
    }
  }, []);

  const changePassword = useCallback(async (req: ChangePasswordRequest): Promise<boolean> => {
    try {
      const res = await apiClient.changePassword(req);
      if (res && res.success) {
        wasAuthenticatedRef.current = false;
        // Password update revokes all database sessions
        setStatus('UNAUTHENTICATED');
        setUsername(null);
        setExpiresAt(null);
        socketManager.disconnect();
        queryClient.clear();
        toast.success(res.message || 'Password updated successfully. Please log in with your new password.');
        return true;
      }
      return false;
    } catch (err: unknown) {
      const apiErr = err as ApiError;
      toast.error(apiErr.message || 'Failed to update password.');
      throw err;
    }
  }, []);

  const clearAuthError = useCallback(() => {
    setAuthError(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        status,
        username,
        expiresAt,
        isLoggingIn,
        authError,
        rateLimitCountdown,
        login,
        logout,
        changePassword,
        retryBootstrap: bootstrapSession,
        clearAuthError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

